import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type {
  PlatformHealthRavenStagingReleaseDetailResponse,
  PlatformHealthRavenStagingReleasesResponse,
  RavenCandidateFlag,
  RavenCheckOutcomeView,
  RavenComponentTransition,
  RavenParitySummary,
  RavenReadMode,
  RavenStagingReleaseRow,
  RavenVerdict,
} from '@/pages/platform-health/types';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, CircleSlash, ExternalLink, RefreshCw, Search } from 'lucide-react';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const REFRESH_INTERVAL_MS = 60 * 1000;

const READ_MODES: readonly RavenReadMode[] = ['native', 'compatibility', 'parity'] as const;

const VERDICTS: readonly ('all' | RavenVerdict)[] = [
  'all',
  'qualified',
  'not_qualified',
  'unknown',
] as const;

const CANDIDATE_FLAGS: readonly RavenCandidateFlag[] = [
  'qualified',
  'not_qualified',
  'qualification_missing',
  'evidence_incomplete',
  'evidence_stale',
  'identity_mismatch',
  'partial_deployment',
  'deployment_incomplete',
  'quarantine_waived',
  'no_op',
  'unclassified_change',
  'no_deployment_observed',
] as const;

/** The flags an operator triages by; each chip narrows the list to `?flag=`. */
const FLAG_CHIPS: readonly RavenCandidateFlag[] = [
  'partial_deployment',
  'deployment_incomplete',
  'evidence_incomplete',
  'quarantine_waived',
  'no_op',
  'unclassified_change',
] as const;

const COMPATIBILITY_NOTE =
  'Compatibility reads project AXON classification evidence and cannot show component receipts, ' +
  'provider proofs, or the qualification outcome set. They never replace native RAVEN evidence.';

const QUALIFIED_BUT_INCOMPLETE_NOTE =
  'Recorded as qualified, but required-evidence coverage is incomplete or unverified.';

const REQUIRED_EVIDENCE_UNKNOWN_NOTE =
  'The required-check set is not recorded for this release; coverage cannot be asserted.';

const RUN_NOT_RECORDED_NOTE = 'producer run not recorded on this release';

const CLASSIFICATION_NOTE =
  'Sealed admission copy; the recorded classification digest is displayed, not re-derived.';

type BadgeVariant = 'neutral' | 'red' | 'amber' | 'green' | 'blue';

function display(value: string | null | undefined) {
  return (value ?? 'unknown').replace(/_/g, ' ');
}

function relative(iso: string | null) {
  if (!iso || Number.isNaN(Date.parse(iso))) return 'time unknown';
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function short(value: string | null, length: number) {
  if (!value) return 'not recorded';
  return value.length > length ? value.slice(0, length) : value;
}

/**
 * A rendering guard, not a verdict: only http(s) identifiers can become anchors. A `urn:`
 * evidence URI is a recorded identifier with no resolvable location and renders as text.
 */
function isReachableUri(uri: string | null | undefined) {
  return typeof uri === 'string' && /^https?:\/\//i.test(uri);
}

function verdictVariant(verdict: RavenVerdict): BadgeVariant {
  if (verdict === 'qualified') return 'green';
  if (verdict === 'not_qualified') return 'red';
  return 'neutral';
}

function snapshotVariant(state: RavenStagingReleaseRow['snapshot']['state']): BadgeVariant {
  if (state === 'succeeded') return 'green';
  if (state === 'partial' || state === 'prepared' || state === 'deploying') return 'amber';
  if (state === 'failed') return 'red';
  return 'neutral';
}

function Badge({
  children,
  variant = 'neutral',
}: {
  children: React.ReactNode;
  variant?: BadgeVariant;
}) {
  const colors = {
    neutral: 'bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-gray-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
    green: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  };
  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        colors[variant]
      )}
    >
      {children}
    </span>
  );
}

function Field({
  label,
  value,
  mono = false,
  danger = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  danger?: boolean;
}) {
  return (
    <div>
      <dt className="text-gray-500">{label}</dt>
      <dd
        className={cn(
          mono ? 'break-all font-mono text-[11px]' : 'mt-0.5 font-medium',
          danger && 'text-red-700 dark:text-red-300'
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-950 dark:text-white">
      {children}
    </h3>
  );
}

function SelectFilter({
  label,
  value,
  values,
  onChange,
}: {
  label: string;
  value: string;
  values: readonly string[];
  onChange?: (value: string) => void;
}) {
  return (
    <label className="text-xs text-gray-600 dark:text-gray-300">
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        className="h-9 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white"
      >
        {values.map((item) => (
          <option key={item} value={item}>
            {item === 'all' ? `All ${label.toLowerCase()}` : display(item)}
          </option>
        ))}
      </select>
    </label>
  );
}

function EvidenceUri({ uri, digest }: { uri: string; digest: string | null }) {
  if (!isReachableUri(uri)) {
    return (
      <span className="break-all font-mono text-[11px] text-gray-600 dark:text-gray-300">
        {uri}
        {digest ? ` · ${digest}` : ''}
      </span>
    );
  }
  return (
    <a
      href={uri}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 break-all font-mono text-[11px] font-medium text-blue-600 hover:underline dark:text-blue-400"
    >
      {uri}
      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
    </a>
  );
}

function ParityPanel({ parity }: { parity: RavenParitySummary }) {
  return (
    <section
      data-testid="raven-parity"
      className="rounded-lg border border-gray-200 bg-white p-3 text-xs dark:border-zinc-700 dark:bg-zinc-800"
    >
      <div className="flex flex-wrap items-center gap-2">
        <SectionHeading>Native / compatibility parity</SectionHeading>
        <Badge variant={parity.comparable ? 'blue' : 'neutral'}>
          {parity.comparable ? 'comparable' : 'not comparable'}
        </Badge>
        {parity.comparable ? (
          <Badge variant={parity.semanticMatch ? 'green' : 'red'}>
            {parity.semanticMatch ? 'semantic match' : 'divergent'}
          </Badge>
        ) : null}
      </div>
      <dl className="mt-2 grid gap-2 sm:grid-cols-4">
        <Field label="Native releases" value={parity.nativeReleaseCount} />
        <Field label="Compatibility releases" value={parity.compatibilityReleaseCount} />
        <Field label="Matched releases" value={parity.matchedReleaseCount} />
        <Field label="Compared fields" value={parity.comparedFields.join(', ') || 'none'} />
      </dl>
      {parity.divergences.length > 0 ? (
        <ul className="mt-3 list-disc space-y-1 pl-5 text-red-700 dark:text-red-300">
          {parity.divergences.map((divergence, index) => (
            <li key={`${divergence.releaseId}:${divergence.field}:${index}`}>
              <span className="font-mono text-[11px]">{divergence.releaseId}</span> ·{' '}
              {divergence.field}: native {divergence.native ?? 'null'} / compatibility{' '}
              {divergence.compatibility ?? 'null'}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-gray-600 dark:text-gray-300">
          No divergence over the matched release set.
        </p>
      )}
      {parity.limitations.length > 0 ? (
        <ul className="mt-3 list-disc space-y-1 pl-5 text-gray-600 dark:text-gray-300">
          {parity.limitations.map((limitation, index) => (
            <li key={`${index}:${limitation}`}>{limitation}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function ComponentRow({ component }: { component: RavenComponentTransition }) {
  return (
    <li className="border-b border-gray-100 px-3 py-2 last:border-b-0 dark:border-zinc-700">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-gray-950 dark:text-white">
          {component.component}
        </span>
        <Badge variant={component.transition === 'inherited' ? 'blue' : 'green'}>
          {component.transition}
        </Badge>
        <span className="text-[11px] text-gray-500">{component.provider}</span>
        {component.mismatches.map((mismatch) => (
          <Badge key={mismatch} variant="red">
            {display(mismatch)}
          </Badge>
        ))}
      </div>
      <dl className="mt-1 grid gap-1 text-[11px] sm:grid-cols-3">
        <Field label="Revision" value={component.revision} mono />
        <Field
          label="Live revision"
          value={component.liveRevision ?? 'not observed'}
          mono
          danger={component.mismatches.includes('revision')}
        />
        <Field
          label="Traffic"
          value={component.trafficPercent === null ? 'not observed' : `${component.trafficPercent}%`}
          danger={component.mismatches.includes('traffic')}
        />
        <Field label="Artifact digest" value={component.artifactDigest ?? 'not recorded'} mono />
        <Field
          label="Provider content digest"
          value={component.providerContentDigest ?? 'not recorded'}
          mono
          danger={component.mismatches.includes('content')}
        />
        <Field label="Provider resource" value={component.providerResource ?? 'not recorded'} mono />
      </dl>
      {component.inheritedFrom ? (
        <p className="mt-1 break-all font-mono text-[10px] text-gray-500">
          inherited from {component.inheritedFrom.releaseId} · {component.inheritedFrom.sourceSha}
        </p>
      ) : null}
    </li>
  );
}

function CheckOutcome({ outcome }: { outcome: RavenCheckOutcomeView }) {
  const statusVariant: BadgeVariant =
    outcome.status === 'pass'
      ? 'green'
      : outcome.status === 'fail' || outcome.status === 'error'
        ? 'red'
        : outcome.status === 'quarantined' || outcome.status === 'missing'
          ? 'amber'
          : 'neutral';
  return (
    <details className="border-b border-gray-100 last:border-b-0 dark:border-zinc-700">
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2 px-3 py-2">
        <span className="font-mono text-[11px] text-gray-950 dark:text-white">
          {outcome.checkId}
        </span>
        <Badge variant={statusVariant}>{outcome.status}</Badge>
        {outcome.required ? <Badge variant="blue">required</Badge> : null}
        {outcome.stale ? <Badge variant="amber">stale</Badge> : null}
        {outcome.flaky ? <Badge variant="amber">flaky</Badge> : null}
        {outcome.quarantine ? (
          <Badge variant={outcome.quarantine.expired ? 'red' : 'amber'}>
            {outcome.quarantine.expired ? 'quarantine expired' : 'quarantined'}
          </Badge>
        ) : null}
      </summary>
      <div className="px-3 pb-3 text-[11px]">
        <dl className="grid gap-1 sm:grid-cols-3">
          {outcome.requiredKnown ? (
            <>
              <Field label="Policy intent" value={display(outcome.policyIntent)} />
              <Field label="Phase" value={display(outcome.phase)} />
              <Field label="Producer" value={display(outcome.producer)} />
            </>
          ) : null}
          <Field label="Valid until" value={outcome.validUntil ?? 'not recorded'} />
          <Field label="Retries" value={outcome.retryCount} />
          <Field label="Failure class" value={outcome.failureClass ?? 'none'} />
          {outcome.testDefinitionMatch === null ? null : (
            <Field
              label="Test definition match"
              value={outcome.testDefinitionMatch ? 'match' : 'mismatch'}
              danger={!outcome.testDefinitionMatch}
            />
          )}
          <Field
            label="Test definition SHA"
            value={outcome.testDefinitionSha ?? 'not recorded'}
            mono
          />
          <Field label="Outcome digest" value={outcome.outcomeDigest ?? 'not recorded'} mono />
          <Field
            label="Attestation digest"
            value={outcome.attestationDigest ?? 'not recorded'}
            mono
          />
        </dl>
        {outcome.requiredKnown && outcome.applicabilityReason ? (
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Applicability: {outcome.applicabilityReason}
          </p>
        ) : null}
        {outcome.evidence.length > 0 ? (
          <ul className="mt-2 space-y-1">
            {outcome.evidence.map((item, index) => (
              <li key={`${item.uri}:${index}`}>
                <span className="text-gray-500">{item.kind ?? 'evidence'}: </span>
                <EvidenceUri uri={item.uri} digest={item.digest} />
              </li>
            ))}
          </ul>
        ) : null}
        <p className="mt-2">
          {outcome.runUrl ? (
            <a
              href={outcome.runUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              Run <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          ) : (
            <span className="text-gray-600 dark:text-gray-300">{RUN_NOT_RECORDED_NOTE}</span>
          )}
        </p>
        {outcome.quarantine ? (
          <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
            <p>
              Quarantine waiver approved by {outcome.quarantine.approvedBy} ·{' '}
              {outcome.quarantine.reason}
            </p>
            <p className="mt-1">
              {outcome.quarantine.linearTicket ?? 'no ticket recorded'} · expires{' '}
              {outcome.quarantine.expiresAt ?? 'not recorded'}
              {outcome.quarantine.expired ? ' (expired)' : ''}
            </p>
            {outcome.quarantine.approvalEvidence ? (
              <p className="mt-1">
                <EvidenceUri
                  uri={outcome.quarantine.approvalEvidence.uri}
                  digest={outcome.quarantine.approvalEvidence.digest}
                />
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </details>
  );
}

function ChipList({ label, values }: { label: string; values: string[] }) {
  return (
    <div>
      <p className="text-gray-500">
        {label} ({values.length})
      </p>
      <div className="mt-1 flex flex-wrap gap-1">
        {values.length === 0 ? (
          <span className="text-gray-500">none</span>
        ) : (
          values.map((value) => (
            <span
              key={value}
              className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-700 dark:bg-zinc-700 dark:text-gray-200"
            >
              {value}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

function ReleaseDetail({
  detail,
}: {
  detail: PlatformHealthRavenStagingReleaseDetailResponse | null;
}) {
  if (!detail) {
    return (
      <div
        data-testid="raven-release-detail"
        className="border-t border-gray-100 bg-gray-50/70 px-4 py-4 text-xs text-gray-500 dark:border-zinc-700 dark:bg-zinc-900/40"
      >
        Loading the recorded evidence for this release…
      </div>
    );
  }
  const { identity, deployment, classification, qualification, requiredEvidence } = detail;
  return (
    <div
      data-testid="raven-release-detail"
      className="border-t border-gray-100 bg-gray-50/70 px-4 py-4 dark:border-zinc-700 dark:bg-zinc-900/40"
    >
      {!detail.available ? (
        <div className="rounded-lg border border-gray-300 bg-gray-50 p-3 text-sm text-gray-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-gray-100">
          <strong>This release&apos;s evidence is unavailable.</strong> An unreadable evidence plane
          is not an empty or successful deploy history.
        </div>
      ) : null}
      {detail.partial ? (
        <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          <strong>Partial release evidence.</strong>
          <ul className="mt-1 list-disc pl-5">
            {detail.gaps.map((gap, index) => (
              <li key={`${gap.code}:${index}`}>{gap.message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {detail.identityMismatches.length > 0 ? (
        <section
          data-testid="raven-identity-mismatches"
          className="mt-4 rounded border border-red-300 bg-red-50 p-3 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100"
        >
          <SectionHeading>Identity mismatches</SectionHeading>
          <ul className="mt-2 space-y-2">
            {detail.identityMismatches.map((mismatch, index) => (
              <li key={`${mismatch.code}:${index}`}>
                <p className="font-semibold">{display(mismatch.code)}</p>
                <p>{mismatch.message}</p>
                <dl className="mt-1 grid gap-1 sm:grid-cols-3">
                  <Field label="Field" value={mismatch.field} mono />
                  <Field label="Expected" value={mismatch.expected ?? 'null'} mono />
                  <Field label="Observed" value={mismatch.observed ?? 'null'} mono />
                </dl>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="mt-4">
        <SectionHeading>Identity</SectionHeading>
        <dl className="mt-4 grid gap-2 rounded border border-gray-200 bg-white p-3 text-xs dark:border-zinc-700 dark:bg-zinc-800 sm:grid-cols-2">
          <Field label="Release ID" value={identity.releaseId} mono />
          <Field label="Source SHA" value={identity.sourceSha ?? 'not recorded'} mono />
          <Field label="Identity digest" value={identity.identityDigest ?? 'not recorded'} mono />
          <Field label="Release branch" value={identity.releaseBranch ?? 'not recorded'} />
          <Field
            label="Base production"
            value={`${identity.baseProduction?.tag ?? 'not recorded'} · ${
              identity.baseProduction?.sha ?? 'not recorded'
            }`}
            mono
          />
          <Field label="Deploy mode" value={display(identity.deployMode)} />
          <Field label="Lockfile digest" value={identity.digests?.lockfile ?? 'not recorded'} mono />
          <Field
            label="Toolchain digest"
            value={identity.digests?.toolchain ?? 'not recorded'}
            mono
          />
          <Field
            label="Deploy policy digest"
            value={identity.digests?.deployPolicy ?? 'not recorded'}
            mono
          />
          <Field
            label="Relevant config digest"
            value={identity.digests?.relevantConfig ?? 'not recorded'}
            mono
          />
          <Field label="Changed paths" value={identity.changedPathCount} />
          <Field
            label="Recorded at"
            value={`${identity.createdAt ?? 'not recorded'} (raw ${
              identity.createdAtRaw ?? 'not recorded'
            })`}
            mono
          />
          <Field label="Selected projects" value={identity.selectedProjects.join(', ') || 'none'} />
          <Field
            label="Selected functions"
            value={identity.selectedFunctions.join(', ') || 'none'}
          />
        </dl>
      </section>

      <section data-testid="raven-component-transitions" className="mt-4">
        <SectionHeading>Deployment &amp; components</SectionHeading>
        <dl className="mt-2 grid gap-2 rounded border border-gray-200 bg-white p-3 text-xs dark:border-zinc-700 dark:bg-zinc-800 sm:grid-cols-2">
          <Field
            label="Chain head snapshot"
            value={deployment.current.snapshotDigest ?? 'not observed'}
            mono
          />
          <Field
            label="Heads pointer"
            value={deployment.headSnapshotDigest ?? 'not recorded'}
            mono
            danger={!deployment.current.matchesHeadsPointer}
          />
          <Field label="Snapshot state" value={display(deployment.current.state)} />
          <Field
            label="Matches heads pointer"
            value={deployment.current.matchesHeadsPointer ? 'yes' : 'no'}
            danger={!deployment.current.matchesHeadsPointer}
          />
          <Field
            label="Chain resolution"
            value={
              deployment.chainResolved
                ? 'resolved from the recorded snapshot chain'
                : `unresolved (${display(deployment.chainIssue)})`
            }
            danger={!deployment.chainResolved}
          />
          <Field label="Attempt" value={deployment.current.attemptId ?? 'not recorded'} mono />
          <Field
            label="Components"
            value={`${deployment.componentCount} total · ${deployment.deployedComponentCount} deployed · ${deployment.inheritedComponentCount} inherited`}
          />
        </dl>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
          {deployment.current.deployRunUrl ? (
            <a
              href={deployment.current.deployRunUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              Deploy run <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          ) : (
            <span className="text-gray-600 dark:text-gray-300">deploy run not recorded</span>
          )}
          {deployment.current.deployRunUrlSource === 'derived_from_attempt_id' ? (
            <span className="text-[10px] text-gray-500">derived from attempt ID</span>
          ) : null}
          {deployment.current.githubDeployment?.url ? (
            <a
              href={deployment.current.githubDeployment.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              Deployment <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          ) : (
            <span className="text-gray-600 dark:text-gray-300">GitHub deployment not recorded</span>
          )}
        </div>
        {deployment.history.length > 0 ? (
          <ol className="mt-2 space-y-1 text-[11px] text-gray-600 dark:text-gray-300">
            {deployment.history.map((entry) => (
              <li key={entry.snapshotDigest} className="break-all font-mono">
                {short(entry.snapshotDigest, 16)} · {display(entry.state)} ·{' '}
                {display(entry.deploymentMode)} · {entry.createdAt ?? 'time unknown'}
                {entry.isChainHead ? ' · chain head' : ''}
              </li>
            ))}
          </ol>
        ) : null}
        {deployment.components.length > 0 ? (
          <ul className="mt-2 rounded border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
            {deployment.components.map((component) => (
              <ComponentRow
                key={`${component.component}:${component.revision}`}
                component={component}
              />
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
            No component receipts were recorded for this snapshot.
          </p>
        )}
      </section>

      <section data-testid="raven-required-evidence" className="mt-4">
        <SectionHeading>Required evidence</SectionHeading>
        {!requiredEvidence.known ? (
          <div className="mt-2 rounded border border-gray-200 bg-white p-3 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-gray-200">
            {REQUIRED_EVIDENCE_UNKNOWN_NOTE}
          </div>
        ) : (
          <div className="mt-2 rounded border border-gray-200 bg-white p-3 text-xs dark:border-zinc-700 dark:bg-zinc-800">
            <div className="grid gap-3 sm:grid-cols-3">
              <ChipList label="Expected" values={requiredEvidence.expectedCheckIds} />
              <ChipList label="Observed" values={requiredEvidence.observedCheckIds} />
              <ChipList label="Missing" values={requiredEvidence.missingCheckIds} />
              <ChipList label="Unexpected" values={requiredEvidence.unexpectedCheckIds} />
              <ChipList label="Stale" values={requiredEvidence.staleCheckIds} />
              <ChipList label="Quarantined" values={requiredEvidence.quarantinedCheckIds} />
              <ChipList label="Failed" values={requiredEvidence.failedCheckIds} />
            </div>
            <p className="mt-2 text-[10px] text-gray-500">
              Expected set sourced from {display(requiredEvidence.knownSource)} · coverage{' '}
              {requiredEvidence.complete ? 'complete' : 'incomplete'}
            </p>
          </div>
        )}
      </section>

      <section data-testid="raven-check-outcomes" className="mt-4">
        <SectionHeading>Check outcomes</SectionHeading>
        {qualification.githubRun?.url ? (
          <p className="mt-1 text-xs">
            <a
              href={qualification.githubRun.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              Qualification run <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          </p>
        ) : null}
        {qualification.outcomes.length === 0 ? (
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
            No check outcomes are recorded for this release.
          </p>
        ) : (
          <div className="mt-2 rounded border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
            {qualification.outcomes.map((outcome) => (
              <CheckOutcome key={outcome.checkId} outcome={outcome} />
            ))}
          </div>
        )}
      </section>

      {qualification.reasonCodeGroups.length > 0 ? (
        <section className="mt-4">
          <SectionHeading>Reason codes</SectionHeading>
          <div className="mt-2 space-y-2 text-xs">
            {qualification.reasonCodeGroups.map((group) => (
              <div key={group.group}>
                <p className="text-gray-500">{display(group.group)}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {group.codes.map((code) => (
                    <span
                      key={code}
                      className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-[10px] text-red-800 dark:bg-red-950 dark:text-red-200"
                    >
                      {code}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="mt-4">
        <SectionHeading>Classification</SectionHeading>
        <dl className="mt-2 grid gap-2 rounded border border-gray-200 bg-white p-3 text-xs dark:border-zinc-700 dark:bg-zinc-800 sm:grid-cols-2">
          <Field label="Classification source" value={display(classification.source)} />
          <Field label="Classification mode" value={display(classification.mode)} />
          <Field label="Completeness" value={display(classification.completeness)} />
          <Field label="Risk classes" value={classification.riskClasses.join(', ') || 'none'} />
          <Field
            label="Classification digest"
            value={classification.classificationDigest ?? 'not recorded'}
            mono
          />
          <Field
            label="Registry digest"
            value={classification.registryDigest ?? 'not recorded'}
            mono
          />
        </dl>
        {classification.unknownPaths.length > 0 ? (
          <div className="mt-2 text-xs">
            <ChipList label="Unclassified paths" values={classification.unknownPaths} />
          </div>
        ) : null}
        {classification.diagnostics.length > 0 ? (
          <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-gray-600 dark:text-gray-300">
            {classification.diagnostics.map((diagnostic, index) => (
              <li key={`${diagnostic.code}:${index}`}>
                <span className="font-mono text-[11px]">{diagnostic.code}</span>:{' '}
                {diagnostic.message}
              </li>
            ))}
          </ul>
        ) : null}
        {classification.coverageFindings.length > 0 ? (
          <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-amber-800 dark:text-amber-200">
            {classification.coverageFindings.map((finding, index) => (
              <li key={`${finding.ruleId}:${index}`}>
                <span className="font-mono text-[11px]">{finding.ruleId}</span> · {finding.severity}{' '}
                · {finding.summary}
              </li>
            ))}
          </ul>
        ) : null}
        <p className="mt-2 text-[10px] text-gray-500">{CLASSIFICATION_NOTE}</p>
      </section>

      <section data-testid="raven-evidence-links" className="mt-4">
        <SectionHeading>Evidence links</SectionHeading>
        {detail.evidenceLinks.length === 0 ? (
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
            No evidence identifiers are recorded for this release.
          </p>
        ) : (
          <ul className="mt-2 space-y-1 text-xs">
            {detail.evidenceLinks.map((link, index) => (
              <li key={`${link.kind}:${link.url}:${index}`}>
                <span className="text-gray-500">{link.label}: </span>
                {link.reachable && isReachableUri(link.url) ? (
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 break-all font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {link.url}
                    <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                  </a>
                ) : (
                  <span className="break-all font-mono text-[11px] text-gray-600 dark:text-gray-300">
                    {link.url}
                    {link.digest ? ` · ${link.digest}` : ''}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ReleaseRow({
  release,
  selected,
  detail,
  onSelect,
}: {
  release: RavenStagingReleaseRow;
  selected: boolean;
  detail: PlatformHealthRavenStagingReleaseDetailResponse | null;
  onSelect?: (releaseId: string | null) => void;
}) {
  // The list projection is structurally weaker than the detail read: detail-scope
  // mismatches (heads pointer, receipt SHA, provider proofs) and per-outcome staleness
  // are not computable from the list, so a row can report `evidenceComplete` while the
  // authoritative detail for the same release reports the opposite. Rendering the weaker
  // claim while the stronger one is on screen is exactly the AC-#2 failure. Both values
  // are API-computed — this selects which recorded verdict to render, it never derives one.
  const authoritative = detail && detail.identity.releaseId === release.releaseId ? detail : null;
  const qualified = authoritative?.qualification.qualified ?? release.qualification.qualified;
  const evidenceComplete = authoritative ? authoritative.evidenceComplete : release.evidenceComplete;
  // F-5: the completeness verdict ships with the scope it was computed at. A row-scoped
  // verdict cannot evaluate every gate term, so it is labelled rather than presented as
  // settled — the same disclosure the row already makes for `listMismatchCount`.
  const evidenceCompleteScope = authoritative
    ? authoritative.evidenceCompleteScope
    : release.evidenceCompleteScope;
  // Union rather than replacement, so a flag the list reported can never be dropped.
  const flags = authoritative
    ? [...new Set([...release.flags, ...authoritative.flags])]
    : release.flags;
  const otherFlags = flags.filter((flag) => flag !== 'qualified' && flag !== 'not_qualified');
  return (
    <details
      data-testid="raven-release-row"
      data-release-id={release.releaseId}
      open={selected || undefined}
      onToggle={(event) => {
        if (!onSelect) return;
        const open = (event.currentTarget as HTMLDetailsElement).open;
        if (open && !selected) onSelect(release.releaseId);
        else if (!open && selected) onSelect(null);
      }}
      className={cn(
        'group border-b border-gray-200 last:border-b-0 dark:border-zinc-700',
        selected && 'bg-blue-50 dark:bg-blue-950/30'
      )}
    >
      <summary className="flex cursor-pointer list-none items-start gap-3 px-4 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={verdictVariant(release.verdict)}>{display(release.verdict)}</Badge>
            <span className="font-mono text-[11px] text-gray-950 dark:text-white">
              {short(release.releaseId, 20)}
            </span>
            <span className="font-mono text-[11px] text-gray-500">
              {short(release.sourceSha, 7)}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Badge variant={snapshotVariant(release.snapshot.state)}>
              {display(release.snapshot.state)}
            </Badge>
            {release.deployMode ? (
              <Badge variant={release.deployMode === 'no-op' ? 'blue' : 'neutral'}>
                {release.deployMode}
              </Badge>
            ) : null}
            {otherFlags.map((flag) => (
              <Badge key={flag} variant="amber">
                {display(flag)}
              </Badge>
            ))}
            {!release.snapshot.chainResolved ? (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                snapshot chain unresolved
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {relative(release.createdAt)} · {release.releaseBranch ?? 'branch not recorded'} ·{' '}
            {release.listMismatchCount} list-scope mismatches ·{' '}
            <span data-testid="raven-evidence-complete-scope">
              {evidenceComplete ? 'evidence complete' : 'evidence incomplete'} (
              {evidenceCompleteScope === 'authoritative'
                ? 'authoritative'
                : 'list-scope — open for the full evidence set'}
              )
            </span>
          </p>
          {qualified && !evidenceComplete ? (
            <p className="mt-2 rounded border border-amber-400 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-900 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-100">
              {QUALIFIED_BUT_INCOMPLETE_NOTE}
            </p>
          ) : null}
        </div>
        <ChevronRight
          className="mt-1 h-4 w-4 shrink-0 text-gray-400 transition-transform group-open:rotate-90"
          aria-hidden="true"
        />
      </summary>
      {selected ? (
        <ReleaseDetail
          detail={detail && detail.identity.releaseId === release.releaseId ? detail : null}
        />
      ) : null}
    </details>
  );
}

export function StagingDeploysContent({
  payload,
  detail = null,
  selectedReleaseId = null,
  verdict = 'all',
  flag = 'all',
  search = '',
  onSelectRelease,
  onSetFilter,
}: {
  payload: PlatformHealthRavenStagingReleasesResponse;
  detail?: PlatformHealthRavenStagingReleaseDetailResponse | null;
  selectedReleaseId?: string | null;
  verdict?: string;
  flag?: string;
  search?: string;
  onSelectRelease?: (releaseId: string | null) => void;
  onSetFilter?: (key: 'verdict' | 'flag' | 'search', value: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div data-testid="raven-read-mode" className="text-xs text-gray-500">
        <p>
          Read mode {payload.readMode} ({payload.readModeSource}) · policy{' '}
          {payload.readPolicyVersion} · generated {relative(payload.generatedAt)}
        </p>
        {payload.readMode === 'compatibility' ? (
          <p className="mt-1 text-amber-700 dark:text-amber-300">{COMPATIBILITY_NOTE}</p>
        ) : null}
      </div>
      {!payload.available ? (
        <div
          data-testid="raven-staging-unavailable"
          className="rounded-lg border border-gray-300 bg-gray-50 p-4 text-sm text-gray-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-gray-100"
        >
          <strong>RAVEN staging release evidence is unavailable.</strong> An unreadable evidence
          plane is not an empty or successful deploy history.
        </div>
      ) : null}
      {payload.partial ? (
        <div
          data-testid="raven-staging-partial"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <strong>Partial release context.</strong>
          <ul className="mt-2 list-disc pl-5 text-xs">
            {payload.gaps.map((gap, index) => (
              <li key={`${gap.code}:${index}`}>{gap.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {payload.parity ? <ParityPanel parity={payload.parity} /> : null}
      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {(
          [
            ['Total', payload.counts.returned],
            ['Qualified', payload.counts.qualified],
            ['Not qualified', payload.counts.notQualified],
            ['Unknown', payload.counts.unknown],
            ['Mismatched', payload.counts.byFlag.identity_mismatch],
          ] as const
        ).map(([label, value]) => (
          <div
            key={label}
            className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800"
          >
            <dt className="text-xs text-gray-500">{label}</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums text-gray-950 dark:text-white">
              {value}
            </dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-wrap gap-2">
        {FLAG_CHIPS.map((chip) => (
          <button
            key={chip}
            type="button"
            aria-label={`Filter by ${display(chip)}`}
            onClick={() => onSetFilter?.('flag', flag === chip ? 'all' : chip)}
            className={cn(
              'rounded-full border px-2.5 py-1 text-[11px] font-medium',
              flag === chip
                ? 'border-blue-400 bg-blue-50 text-blue-800 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-200'
                : 'border-gray-300 bg-white text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-gray-200'
            )}
          >
            {display(chip)} · {payload.counts.byFlag[chip]}
          </button>
        ))}
      </div>
      <section
        aria-label="Release filters"
        className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800"
      >
        <div className="flex flex-wrap gap-2">
          <label className="relative min-w-52 flex-1">
            <span className="sr-only">Search releases</span>
            <Search
              className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400"
              aria-hidden="true"
            />
            <input
              aria-label="Search releases"
              value={search}
              onChange={(event) => onSetFilter?.('search', event.target.value)}
              placeholder="Release ID, source SHA, or release branch"
              className="h-9 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
            />
          </label>
          <SelectFilter
            label="Verdict"
            value={verdict}
            values={VERDICTS}
            onChange={(value) => onSetFilter?.('verdict', value)}
          />
          <SelectFilter
            label="Flag"
            value={flag}
            values={['all', ...CANDIDATE_FLAGS]}
            onChange={(value) => onSetFilter?.('flag', value)}
          />
        </div>
      </section>
      {payload.available && payload.releases.length === 0 ? (
        <div
          data-testid="raven-staging-empty"
          className="rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-zinc-700 dark:bg-zinc-800"
        >
          <CircleSlash className="mx-auto h-8 w-8 text-gray-400" aria-hidden="true" />
          <p className="mt-4 text-sm font-medium text-gray-950 dark:text-white">
            No staging releases match these filters
          </p>
          <p className="mt-1 text-xs text-gray-500">
            An empty list is a claim about observed evidence, not an all-clear.
          </p>
        </div>
      ) : null}
      {payload.releases.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
          {payload.releases.map((release) => (
            <ReleaseRow
              key={release.releaseId}
              release={release}
              selected={selectedReleaseId === release.releaseId}
              detail={detail}
              onSelect={onSelectRelease}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function StagingDeploysPage() {
  const [params, setParams] = useSearchParams();
  const requestedReadMode = params.get('readMode') as RavenReadMode | null;
  const readMode: RavenReadMode =
    requestedReadMode && READ_MODES.includes(requestedReadMode) ? requestedReadMode : 'native';
  const verdict = params.get('verdict') ?? 'all';
  const flag = params.get('flag') ?? 'all';
  const search = params.get('search') ?? '';
  const selectedReleaseId = params.get('release');

  const listQuery = useQuery({
    queryKey: ['platform-health', 'raven', 'staging-releases', verdict, flag, search, readMode],
    queryFn: () =>
      api.getPlatformHealthRavenStagingReleases({
        verdict: verdict === 'all' ? undefined : verdict,
        flag: flag === 'all' ? undefined : flag,
        search: search || undefined,
        readMode,
      }),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
  const payload = listQuery.data?.data;
  const effectiveReleaseId = selectedReleaseId ?? payload?.releases[0]?.releaseId ?? null;

  useEffect(() => {
    if (!selectedReleaseId && effectiveReleaseId)
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('release', effectiveReleaseId);
          return next;
        },
        { replace: true }
      );
  }, [effectiveReleaseId, selectedReleaseId, setParams]);

  const detailQuery = useQuery({
    queryKey: ['platform-health', 'raven', 'staging-release', effectiveReleaseId, readMode],
    queryFn: () => api.getPlatformHealthRavenStagingRelease(effectiveReleaseId!, readMode),
    enabled: !!effectiveReleaseId,
    retry: false,
  });

  const update = (key: string, value: string | null) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (!value || value === 'all') next.delete(key);
      else next.set(key, value);
      if (key !== 'release') next.delete('release');
      return next;
    });

  const selectedGithubUrl = detailQuery.data?.data.deployment.current.githubDeployment?.url ?? null;

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Staging Deploys"
        subtitle="RAVEN release identity, component deployment, required evidence, and the staging qualification decision."
      >
        <div className="flex items-center gap-2">
          <select
            aria-label="Read mode"
            value={readMode}
            onChange={(event) => update('readMode', event.target.value)}
            className="rounded border bg-white px-2 py-1 text-sm dark:bg-zinc-800"
          >
            <option value="native">Native</option>
            <option value="compatibility">Compatibility</option>
            <option value="parity">Parity</option>
          </select>
          <button
            type="button"
            aria-label="Refresh"
            onClick={() => void Promise.all([listQuery.refetch(), detailQuery.refetch()])}
            className="inline-flex items-center gap-1 rounded border px-2 py-1 text-sm"
          >
            <RefreshCw
              className={cn('h-4 w-4', listQuery.isFetching && 'animate-spin')}
              aria-hidden="true"
            />
            Refresh
          </button>
          {selectedGithubUrl ? (
            <a
              href={selectedGithubUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              GitHub deployment
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          ) : null}
        </div>
      </PageHeader>
      {listQuery.isError ? (
        <div
          data-testid="raven-staging-error"
          className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          Couldn&apos;t load RAVEN staging releases:{' '}
          {(listQuery.error as Error)?.message || 'unknown error'}
        </div>
      ) : null}
      {listQuery.isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center text-sm text-gray-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-gray-400">
          Loading RAVEN staging release evidence…
        </div>
      ) : null}
      {payload ? (
        <StagingDeploysContent
          payload={payload}
          detail={detailQuery.data?.data ?? null}
          selectedReleaseId={effectiveReleaseId}
          verdict={verdict}
          flag={flag}
          search={search}
          onSelectRelease={(releaseId) => update('release', releaseId)}
          onSetFilter={(key, value) => update(key, value)}
        />
      ) : null}
    </div>
  );
}
